/**
 * Roast's DEMO catalog — the products and collections a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, for one reason: the BUILDER needs it too. A seller
 * opening Roast on a store with no products would otherwise get the house fallback — a clothing shop,
 * "Cotton Crew Tee" and stock photos of coat racks — inside a coffee theme. The theme's own world has to
 * travel with the theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: this module must stay free of any section import, because
 * `sections/_shared/product.ts` reads it. Anything else here would close an import cycle.
 *
 * Prices take the STORE's currency rather than carrying their own: the same demo has to read correctly
 * in a MAD shop and in a EUR one, and a demo that shows the wrong currency teaches the seller nothing.
 */
const IMG = '/demo/coffee';
/** Le vrai choix d'un acheteur de café : la mouture, pas la taille. */
const GRINDS = ['Grains entiers', 'Espresso', 'Filtre'];
function product(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [{ url: `${IMG}/${p.image}`, alt: p.alt, isCover: true }],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'SBAH',
        rating: p.rating,
        options: [{ name: 'Mouture', values: (p.grinds ?? GRINDS).map((label) => ({ label })) }],
    };
}
/** The SBAH range, in merchandising order. */
export function sbahDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-sbah-ethiopie',
            name: 'Éthiopie Yirgacheffe — 250 g',
            slug: 'ethiopie-yirgacheffe-250g',
            price: 13900,
            compareAt: 15900,
            badge: 'custom-badge-1',
            image: 'product-ethiopie.webp',
            alt: 'Sachet SBAH Éthiopie Yirgacheffe',
            rating: { value: 4.9, count: 284 },
            desc: 'Floral et vif — jasmin, bergamote, thé noir. Torréfaction claire.',
        }),
        product(currency, {
            id: 'p-sbah-colombie',
            name: 'Colombie Huila — 250 g',
            slug: 'colombie-huila-250g',
            price: 12900,
            image: 'product-colombie.webp',
            alt: 'Sachet SBAH Colombie Huila',
            rating: { value: 4.8, count: 196 },
            desc: 'L’équilibré de la gamme — caramel, amande, pomme mûre.',
        }),
        product(currency, {
            id: 'p-sbah-bresil',
            name: 'Brésil Cerrado — 250 g',
            slug: 'bresil-cerrado-250g',
            price: 11900,
            image: 'product-bresil.webp',
            alt: 'Sachet SBAH Brésil Cerrado',
            rating: { value: 4.7, count: 241 },
            desc: 'Cacao et noisette grillée, sans acidité. Le café du matin.',
        }),
        product(currency, {
            id: 'p-sbah-espresso',
            name: 'Mélange Espresso — 250 g',
            slug: 'melange-espresso-250g',
            price: 10900,
            compareAt: 12900,
            badge: 'custom-badge-2',
            image: 'product-espresso.webp',
            alt: 'Sachet SBAH Mélange Espresso et tasse',
            rating: { value: 4.8, count: 318 },
            desc: 'Notre assemblage maison, calibré pour la machine à porte-filtre.',
        }),
        product(currency, {
            id: 'p-sbah-corse',
            name: 'Nuit Blanche — torréfaction corsée',
            slug: 'nuit-blanche-corse-250g',
            price: 11900,
            image: 'product-corse.webp',
            alt: 'Sachet SBAH Nuit Blanche, torréfaction très foncée',
            rating: { value: 4.6, count: 97 },
            desc: 'Foncé, puissant, sans détour. Pour ceux qui le boivent noir.',
        }),
        product(currency, {
            id: 'p-sbah-decafeine',
            name: 'Décaféiné Sidamo — 250 g',
            slug: 'decafeine-sidamo-250g',
            price: 13900,
            image: 'product-decafeine.webp',
            alt: 'Sachet SBAH Décaféiné Sidamo',
            rating: { value: 4.7, count: 64 },
            desc: 'Décaféiné à l’eau, sans solvant. Le goût reste, la nuit aussi.',
        }),
        product(currency, {
            id: 'p-sbah-moulu',
            name: 'Filtre du Jour — moulu, 250 g',
            slug: 'filtre-du-jour-moulu-250g',
            price: 9900,
            image: 'product-moulu.webp',
            alt: 'Sachet SBAH Filtre du Jour et mouture',
            rating: { value: 4.6, count: 152 },
            desc: 'Déjà moulu, prêt pour la cafetière. Le lot change chaque semaine.',
            grinds: ['Filtre'],
        }),
        product(currency, {
            id: 'p-sbah-coffret',
            name: 'Coffret Découverte — 3 × 100 g',
            slug: 'coffret-decouverte-3x100g',
            price: 24900,
            compareAt: 29900,
            badge: 'custom-badge-1',
            image: 'product-coffret.webp',
            alt: 'Coffret SBAH de trois petits sachets',
            rating: { value: 4.9, count: 128 },
            desc: 'Les trois origines, en petit format, pour trouver la vôtre.',
            grinds: ['Grains entiers'],
        }),
    ];
}
/** Roast merchandises by BREWING METHOD, not by origin — that is how a coffee shelf is browsed. */
export function sbahDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-espresso', 'Espresso', 'espresso', 'method-espresso.webp', 'Porte-filtre et espresso fraîchement extrait', 4, 'Torréfactions plus foncées, calibrées pour la machine à porte-filtre.'),
        make('c-filtre', 'Filtre & V60', 'filtre-v60', 'method-filtre.webp', 'Cafetière à filtre conique et bouilloire à col de cygne', 4, 'Torréfactions claires, aromatiques, pour les méthodes douces.'),
        make('c-italienne', 'Cafetière italienne', 'cafetiere-italienne', 'method-italienne.webp', 'Cafetière italienne sur le feu', 3, 'Mouture fine et corps rond — la cafetière de tous les jours.'),
        make('c-grains', 'Grains entiers', 'grains-entiers', 'method-grains.webp', 'Grains de café entiers dans un bol en bois', 5, 'Pour ceux qui moulent au dernier moment. C’est toujours le meilleur choix.'),
    ];
}
//# sourceMappingURL=catalog.js.map